// ---------------------------------------------------------------------------
// Expected GraphQL errors — legitimate domain states, not failures.
//
// Some reads can surface an "empty" domain state as a GraphQL error instead of
// a null payload (e.g. the not-found family on `entityPolicyAssignment` for a
// freshly created entity that simply has no compliance profile yet). Those are
// part of the operation's contract: the calling hook translates them into an
// empty result, so the global client must not console.error them or report
// them to Sentry (ENT-445 F-001).
//
// This module is deliberately side-effect free (no keycloak/Sentry imports) so
// hooks and tests can share the registry while `@/lib/graphql_client` is mocked.
// Add entries sparingly and per-operation — anything not listed still logs loudly.
// ---------------------------------------------------------------------------

/** The minimal error shape we need (graphql-request's GraphQLError is assignable). */
export interface GqlErrorLike {
  extensions?: { code?: unknown; errorType?: unknown }
  /** GraphQL's own per-error field path (e.g. `['policyBundleAssignedEntities','items',3,'owner']`).
   * Only present/needed for a registry entry that scopes by `pathEndsWith`. */
  path?: readonly (string | number)[]
}

/**
 * A registry entry can be a plain code list (matches ANY path — every entry before T05 relied on
 * this implicitly, and it must keep working exactly as before), or a rule that ALSO requires the
 * error's `path` to end in a specific segment. Use the richer form whenever the code alone is too
 * coarse to safely mean "expected" everywhere it could appear on that operation (ENT-520 T05 —
 * `INTERNAL_SERVER_ERROR` is a generic gateway code, not unique to one failure mode).
 */
export interface ExpectedGqlErrorRule {
  codes: readonly string[]
  /** Only expected when the error's `path` ends with this segment. Omit for no path constraint. */
  pathEndsWith?: string
}

export type ExpectedGqlErrorEntry = readonly string[] | ExpectedGqlErrorRule

/**
 * Operation name → the error(s) that are an expected domain state for it.
 * Codes match `extensions.errorType` (task-framework's semantic name, e.g.
 * `TF_BUNDLE_NOT_FOUND`) falling back to `extensions.code` (gateway/infra codes,
 * e.g. `NOT_FOUND`) — the same precedence as `gqlErrorCode` in the Compliance pages.
 */
export const EXPECTED_GQL_ERRORS: Record<string, ExpectedGqlErrorEntry> = {
  // A fresh entity with no compliance profile: the BE read is nullable by
  // contract (task-framework-svc PolicyBundleResolver.entityPolicyAssignment
  // returns null), but the not-found family can still surface as an error —
  // useEntityPolicyAssignment maps it to `entityPolicyAssignment: null`.
  EntityPolicyAssignment: ['NOT_FOUND', 'TF_BUNDLE_NOT_FOUND'],
  // ENT-520 (T05, DD D5) — an archived/unresolvable owner on one row of
  // `policyBundleAssignedEntities` surfaces as a per-item GraphQL error (path
  // ending in `…owner`) alongside a PARTIAL data payload (`owner: null` for
  // that row), not a fully-failed response. The exact code, taken from a live
  // probe against dev (Zenith tenant, UK Standard Compliance, 2026-09-11 — not
  // a guess, not the BE resolver's own comment): `INTERNAL_SERVER_ERROR`. That
  // code is the GATEWAY'S OWN flattening of BE's actual `LEGAL_ENTITY_NOT_FOUND`
  // — a finding for this task's Status section, not something FE controls; a
  // future gateway/BE change could propagate the specific code, at which point
  // `pathEndsWith` here could be dropped in favour of a unique code alone.
  //
  // `INTERNAL_SERVER_ERROR` is a generic code that could mean an unrelated real
  // failure ANYWHERE on this operation — a first cut of this entry registered
  // the bare code with no path constraint, which would have silenced console
  // .error/Sentry reporting for a genuine server failure too (caught before
  // merge, not shipped). `pathEndsWith: 'owner'` is what makes this safe: only
  // an error on exactly this field path is treated as the known archived-owner
  // case. Kept in lockstep with `isRecoverableOwnerError` in
  // `compliance-templates.ts`, which applies the identical code+path check to
  // decide whether to RECOVER the partial data — two call sites, one rule.
  PolicyBundleAssignedEntities: { codes: ['INTERNAL_SERVER_ERROR'], pathEndsWith: 'owner' },
  // ENT-558 (help-request-internal-tab-crash-unstaffed-viewer) — a cross-tenant viewer with
  // readAll (desk) permission but no internal-channel STAFFING is expected, by design (D-8), to
  // get a participancy denial from chat-integration-svc on either list: CI_HELP_REQUEST_FORBIDDEN
  // ("not a participant") or CI_HELP_REQUEST_NOT_FOUND. Both resolvers correctly never enter the
  // cross-tenant widening path for this case — confirmed server-side (be T0004 log trace) — so
  // this is a known domain state, not a failure to report. The FE side (HelpRequestInvitePicker /
  // HelpRequestMentionPicker) already turns `isError` into a disabled control with explanatory
  // copy via `helpRequestErrorMessage`; this entry only quiets the console.error/Sentry noise.
  HelpRequestInviteListV2: ['CI_HELP_REQUEST_FORBIDDEN', 'CI_HELP_REQUEST_NOT_FOUND'],
  HelpRequestMentionsListV2: ['CI_HELP_REQUEST_FORBIDDEN', 'CI_HELP_REQUEST_NOT_FOUND'],
  // CI-HCR (help-request-channel-retry T02) — both are refusals the server documents as
  // `isExpected: true` (handover §2): the request is at its lifetime manual-retry budget, or one
  // accepted retry has already happened this window. `CI_WORKFLOW_START_TIMEOUT` is deliberately
  // NOT listed here — the handover calls that one out as an incident, not a refusal.
  RetryHelpRequestChannelProvisioning: ['CI_MANUAL_RETRY_BUDGET_EXHAUSTED', 'CI_RETRY_REQUESTED_TOO_SOON'],
}

/** The semantic code of one GraphQL error (errorType first, then code). */
function codeOf(error: GqlErrorLike): string | undefined {
  const ext = error.extensions
  const raw = ext?.errorType ?? ext?.code
  return typeof raw === 'string' ? raw : undefined
}

export function asRule(entry: ExpectedGqlErrorEntry): ExpectedGqlErrorRule {
  return 'codes' in entry ? entry : { codes: entry }
}

/**
 * True when EVERY error in a response is an expected domain state for the
 * operation — only then may the client skip its error logging. A mixed
 * response (one expected + one real failure, or one expected-code error on an
 * unexpected path) still logs.
 */
export function areExpectedGqlErrors(
  operationName: string | undefined,
  errors: readonly GqlErrorLike[],
): boolean {
  const raw = operationName ? EXPECTED_GQL_ERRORS[operationName] : undefined
  if (!raw || errors.length === 0) return false
  const rule = asRule(raw)
  if (rule.codes.length === 0) return false
  return errors.every((e) => {
    const code = codeOf(e)
    if (code === undefined || !rule.codes.includes(code)) return false
    if (rule.pathEndsWith === undefined) return true
    const path = e.path
    return !!path && path.length > 0 && path[path.length - 1] === rule.pathEndsWith
  })
}
